'use client'

import React, { useState, useEffect } from 'react'
import { useDict } from '@/lib'
import s from './Footer.module.scss'
import { BasicWrapper } from '@/wrappers/BasicWrapper'
import { basicData } from '@/constants/basicData'
import { usePathname } from 'next/navigation'
import { classnames } from '@/utils/classnames'
import Image from 'next/image'
import { ButtonOrLink } from '@/components/ButtonOrLink'
import { SocialLinks } from '@/components/SocialIcon'
import { useNavbarLogoSrc } from '@/hooks/useZebraLogoSrc'

interface CompanyData {
  name?: string
  street?: string
  buildingNumber?: string
  city?: string
  postalCode?: string
}

export function Footer() {
  const pathname = usePathname()
  const zebraLogoSrc = useNavbarLogoSrc()
  const dict = useDict('footer')
  const [companyData, setCompanyData] = useState<CompanyData>({})
  const [loading, setLoading] = useState(true)

  const normalize = (path: string) => path.replace(/\/+$/, '') || '/'

  // Załaduj dane firmy
  useEffect(() => {
    const loadCompanyData = async () => {
      try {
        const response = await fetch('/api/global-links')
        if (response.ok) {
          const data = await response.json()
          setCompanyData(data.company || {})
        }
      } catch (error) {
        console.error('Error loading company data:', error)
      } finally {
        setLoading(false)
      }
    }

    loadCompanyData()
  }, [])

  if (!dict) {
    return null // lub loading spinner
  }

  return (
    <div id="app-footer-start" className={s.siteFooter}>
      <BasicWrapper>
        <footer className={s.container}>
          <div className={s.sides}>
            <div className={s.column}>
              <ButtonOrLink
                as="link"
                href="/"
                variant="socials"
                classes={s.logo}
              >
                <Image
                  src={zebraLogoSrc}
                  alt="Zebra Logo"
                  width={120}
                  height={35}
                  className={s.logo}
                  priority
                />
              </ButtonOrLink>
              <p className={s.company}>
                {companyData.name || basicData.companyName}
              </p>
              
              {/* Dane adresowe 1: ulica i numer budynku */}
              {(companyData.street || companyData.buildingNumber) && (
                <p className={s.addressLine}>
                  {companyData.street 
                    ? `ul. ${[companyData.street, companyData.buildingNumber].filter(Boolean).join(' ')}`
                    : companyData.buildingNumber || 'Dane adresowe 1'}
                </p>
              )}
              
              {/* Dane adresowe 2: kod pocztowy i miasto */}
              {(companyData.postalCode || companyData.city) && (
                <p className={s.addressLine}>
                  {[companyData.postalCode, companyData.city]
                    .filter(Boolean)
                    .join(' ') || 'Dane adresowe 2'}
                </p>
              )}
              
              {/* Fallback do hardcodowanych danych jeśli brak danych z bazy */}
              {!loading && !companyData.name && !companyData.street && !companyData.city && 
                basicData.addressLines.map((line, i) => (
                  <p key={i} className={s.addressLine}>
                    {line}
                  </p>
                ))
              }

              {/* Nowy SocialLinks komponent */}
              <div className={s.socials}>
                <SocialLinks
                  socials={[
                    'facebook',
                    'instagram',
                    'x',
                    'tiktok',
                    'threads',
                    'linkedin',
                  ]}
                  useGlobalSocials={true}
                />
              </div>
            </div>
          </div>

          <div className={s.sides}>
            <div className={s.column}>
              <p className={s.columnTitle}>{dict.menu.title}</p>
              {dict.menu.links.map((link: any, i: number) => (
                <ButtonOrLink
                  as="link"
                  href={link.path}
                  key={i}
                  variant="menuLink"
                  classes={classnames(s.link, s.pulseLink, {
                    [s.active]:
                      link.path === '/' || link.path === ''
                        ? normalize(pathname) === '/'
                        : normalize(pathname).startsWith(normalize(link.path)),
                  })}
                >
                  {link.text}
                </ButtonOrLink>
              ))}
            </div>

            <div className={s.column}>
              <p className={s.columnTitle}>{dict.help.title}</p>
              {dict.help.links.map((link: any, i: number) => (
                <ButtonOrLink
                  as="link"
                  href={link.path}
                  key={i}
                  variant="menuLink"
                  classes={classnames(s.link, s.pulseLink, {
                    [s.active]: normalize(pathname).startsWith(
                      normalize(link.path)
                    ),
                  })}
                >
                  {link.text}
                </ButtonOrLink>
              ))}
            </div>
          </div>
        </footer>
      </BasicWrapper>
      <div className={s.footerBottom}>
        <div className={s.root}>
          <div className={s.bottomContainer}>
            <div className={s.line} />
            <div className={s.copyright}>
              {dict.copyright.normal} <b>{dict.copyright.bold}</b>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
